import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";

const initialState = {
  // Export status
  exportStatus: "idle",
  exportError: null,

  // Export data storage
  currentExportData: null,
  exportHistory: [],

  // Export types
  availableExportTypes: [
    "synthesis_result",
    "synthesis_treasury",
    "writing_list",
  ],

  // Last export result
  lastExportResult: null,
  downloadUrl: null,
};

// Export synthesis result data
export const exportSynthesisResult = createAsyncThunk(
  "exports/exportSynthesisResult",
  async ({ projectId, tableType, mode, exportData }, thunkAPI) => {
    try {
      const response = await axios({
        method: "get",
        url: `/api/projects/${projectId}/exports/${tableType}/synthese/${mode}`,
        params: {
          startDate: exportData.startDate,
          endDate: exportData.endDate,
          maxCategoryDepth: exportData.maxCategoryDepth + "",
          at: exportData.at,
          filterOptions: exportData.filterOptions,
          objective: exportData.objective,
          excludeMaskedEmptyCategories:
            exportData.excludeMaskedEmptyCategories !== false ? "true" : "false",
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "synthese_result.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
      if (response.status === 200) {
        return response.data;
      }

      throw new Error(response.statusText);
    } catch (err) {
      console.error("Export synthesis result error:", err);
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message || "Erreur lors de l'export"
      );
    }
  }
);

// Export synthesis treasury data
export const exportSynthesisTreasury = createAsyncThunk(
  "exports/exportSynthesisTreasury",
  async (
    { projectId, tableType = "categories", mode = "treso", exportData },
    thunkAPI
  ) => {
    try {
      const response = await axios({
        method: "get",
        url: `/api/projects/${projectId}/exports/${tableType}/synthese/${mode}`,
        params: {
          startDate: exportData.startDate,
          endDate: exportData.endDate,
          maxCategoryDepth: exportData.maxCategoryDepth + "",
          at: exportData.at,
          filterOptions: exportData.filterOptions,
          excludeMaskedEmptyCategories:
            exportData.excludeMaskedEmptyCategories !== false ? "true" : "false",
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "synthese_treasury.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
      if (response.status === 200) {
        return response.data;
      }

      throw new Error(response.statusText);
    } catch (err) {
      console.error("Export synthesis treasury error:", err);
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message || "Erreur lors de l'export"
      );
    }
  }
);

// Export writing list data
export const exportWritingList = createAsyncThunk(
  "exports/exportWritingList",
  async ({ projectId, tableType = "categories", mode, exportData }, thunkAPI) => {
    try {
      const response = await axios({
        method: "get",
        url: `/api/projects/${projectId}/exports/${tableType}/list-ecritures/${mode}`,
        params: {
          startDate: exportData.startDate,
          endDate: exportData.endDate,
          filterOptions: exportData.filterOptions,
          monthEndBalanceItem: exportData.monthEndBalanceItem,
        },
        responseType: "blob",
      });

      // Create blob and download file
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const monthEndBalanceItemFileLabels = {
        outstandingReceivables: "reste_a_encaisser",
        outstandingPayables: "reste_a_depenser",
        prepaidExpenses: "decaissements_realises_d_avance",
        deferredRevenue: "encaissements_realises_d_avance",
      };
      const fileSuffix = exportData.monthEndBalanceItem
        ? `_${
            monthEndBalanceItemFileLabels[exportData.monthEndBalanceItem] ||
            exportData.monthEndBalanceItem
          }`
        : "";
      a.download = `liste_ecritures${fileSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);

      if (response.status === 200) {
        return response.data;
      }

      throw new Error(response.statusText);
    } catch (err) {
      console.error("Export writing list error:", err);
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message || "Erreur lors de l'export"
      );
    }
  }
);

export const exportMonthEndBalanceSheet = createAsyncThunk(
  "exports/exportMonthEndBalanceSheet",
  async ({ projectId, month }, thunkAPI) => {
    try {
      const response = await axios({
        method: "get",
        url: `/api/projects/${projectId}/exports/month-end-balance-sheet`,
        params: { month },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bilan_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);

      if (response.status === 200) {
        return response.data;
      }

      throw new Error(response.statusText);
    } catch (err) {
      console.error("Export month-end balance sheet error:", err);
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || err.message || "Erreur lors de l'export"
      );
    }
  }
);

const slice = createSlice({
  name: "exports",
  initialState,
  reducers: {
    // Store current export configuration
    setCurrentExportData: (state, action) => {
      state.currentExportData = action.payload;
    },

    // Clear export data
    clearExportData: (state) => {
      state.currentExportData = null;
      state.exportError = null;
      state.lastExportResult = null;
      state.downloadUrl = null;
    },

    // Reset export status
    resetExportStatus: (state) => {
      state.exportStatus = "idle";
      state.exportError = null;
    },

    // Add to export history
    addToExportHistory: (state, action) => {
      state.exportHistory.unshift({
        ...action.payload,
        timestamp: new Date().toISOString(),
      });

      // Keep only last 10 exports
      if (state.exportHistory.length > 10) {
        state.exportHistory = state.exportHistory.slice(0, 10);
      }
    },

    // Clear export history
    clearExportHistory: (state) => {
      state.exportHistory = [];
    },
  },
  extraReducers: (builder) => {
    // Export Synthesis Result
    builder
      .addCase(exportSynthesisResult.pending, (state) => {
        state.exportStatus = "loading";
        state.exportError = null;
      })
      .addCase(exportSynthesisResult.fulfilled, (state, action) => {
        state.exportStatus = "succeeded";
        state.lastExportResult = action.payload;
        state.downloadUrl = action.payload.downloadUrl;

        // Add to history
        state.exportHistory.unshift({
          type: "synthesis_result",
          timestamp: new Date().toISOString(),
          status: "success",
          message: action.payload.message,
          downloadUrl: action.payload.downloadUrl,
        });
      })
      .addCase(exportSynthesisResult.rejected, (state, action) => {
        state.exportStatus = "failed";
        state.exportError = action.payload;

        // Add to history
        state.exportHistory.unshift({
          type: "synthesis_result",
          timestamp: new Date().toISOString(),
          status: "error",
          message: action.payload,
        });
      });

    // Export Synthesis Treasury
    builder
      .addCase(exportSynthesisTreasury.pending, (state) => {
        state.exportStatus = "loading";
        state.exportError = null;
      })
      .addCase(exportSynthesisTreasury.fulfilled, (state, action) => {
        state.exportStatus = "succeeded";
        state.lastExportResult = action.payload;
        state.downloadUrl = action.payload.downloadUrl;

        // Add to history
        state.exportHistory.unshift({
          type: "synthesis_treasury",
          timestamp: new Date().toISOString(),
          status: "success",
          message: action.payload.message,
          downloadUrl: action.payload.downloadUrl,
        });
      })
      .addCase(exportSynthesisTreasury.rejected, (state, action) => {
        state.exportStatus = "failed";
        state.exportError = action.payload;

        // Add to history
        state.exportHistory.unshift({
          type: "synthesis_treasury",
          timestamp: new Date().toISOString(),
          status: "error",
          message: action.payload,
        });
      });

    // Export Writing List
    builder
      .addCase(exportWritingList.pending, (state) => {
        state.exportStatus = "loading";
        state.exportError = null;
      })
      .addCase(exportWritingList.fulfilled, (state, action) => {
        state.exportStatus = "succeeded";
        state.lastExportResult = action.payload;
        state.downloadUrl = action.payload.downloadUrl;

        // Add to history
        state.exportHistory.unshift({
          type: "writing_list",
          timestamp: new Date().toISOString(),
          status: "success",
          message: action.payload.message,
          downloadUrl: action.payload.downloadUrl,
        });
      })
      .addCase(exportWritingList.rejected, (state, action) => {
        state.exportStatus = "failed";
        state.exportError = action.payload;

        // Add to history
        state.exportHistory.unshift({
          type: "writing_list",
          timestamp: new Date().toISOString(),
          status: "error",
          message: action.payload,
        });
      });

    builder
      .addCase(exportMonthEndBalanceSheet.pending, (state) => {
        state.exportStatus = "loading";
        state.exportError = null;
      })
      .addCase(exportMonthEndBalanceSheet.fulfilled, (state, action) => {
        state.exportStatus = "succeeded";
        state.lastExportResult = action.payload;
      })
      .addCase(exportMonthEndBalanceSheet.rejected, (state, action) => {
        state.exportStatus = "failed";
        state.exportError = action.payload;
      });
  },
});

export const {
  setCurrentExportData,
  clearExportData,
  resetExportStatus,
  addToExportHistory,
  clearExportHistory,
} = slice.actions;

export const reducer = slice.reducer;

export default slice;
